import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, CheckCircle2, Truck, MapPin, Navigation, Calculator, Loader2, Car, Tractor, Bike, Container, Moon } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VEHICLE_TYPES = [
  { id: "putnicko", label: "Putničko vozilo", icon: Car, base: 2500, perKm: 110 },
  { id: "teretno", label: "Teretno vozilo", icon: Truck, base: 4500, perKm: 150 },
  { id: "radne_masine", label: "Radne mašine", icon: Tractor, base: 5000, perKm: 200 },
  { id: "dvotockasi", label: "Dvotočkaši", icon: Bike, base: 1800, perKm: 70 },
  { id: "prikljucne_masine", label: "Priključne mašine", icon: Container, base: 3500, perKm: 150 },
];

const TOW_IMG =
  "https://images.unsplash.com/photo-1730514785075-b065c757b653?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwzfHx0b3clMjB0cnVjayUyMGNhciUyMHRvd2luZyUyMHZlaGljbGUlMjBoYXVsaW5nfGVufDB8fHx8MTc5MDIxNTA2OXww&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  "Dostupni 365 dana u godini (00-24h)",
  "Siguran transport bez dodatnih oštećenja",
  "Prevoz havarisanih i neregistrovanih vozila",
  "Dolazak na lokaciju u najkraćem roku",
];

export default function TowingSpotlight() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  const [pickup, setPickup] = useState("");
  const [destination, setDestination] = useState("");
  const [vehicleType, setVehicleType] = useState("putnicko");
  const isNightNow = () => { const h = new Date().getHours(); return h >= 22 || h < 6; };
  const [night, setNight] = useState(isNightNow());
  const [loading, setLoading] = useState(false);
  const [estimate, setEstimate] = useState(null);

  const calculate = async () => {
    if (!pickup.trim() || !destination.trim()) {
      toast.error("Unesite mesto preuzimanja i destinaciju");
      return;
    }
    setLoading(true);
    setEstimate(null);
    try {
      const { data } = await axios.get(`${API}/towing/estimate`, {
        params: { pickup, destination, vehicle_type: vehicleType, night },
      });
      setEstimate(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Procena trenutno nije dostupna — pozovite nas.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="slep-sluzba" data-testid="towing-spotlight-section" ref={ref}
      className="relative py-24 lg:py-32 px-6 lg:px-12 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#09090B] via-[rgba(227,22,30,0.06)] to-[#09090B]" />

      <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <span className="inline-flex items-center gap-2 text-red-400 text-xs font-bold tracking-[0.3em] uppercase">
            <Truck size={14} /> Non-stop pomoć na putu
          </span>
          <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl text-white mt-4 leading-[0.95]">
            Hitna Šlep Služba <span className="text-red-500">&</span> Prevoz Vozila
          </h2>
          <p className="text-zinc-400 mt-6 leading-relaxed max-w-lg">
            Pokvareno vozilo, udes ili kupovina automobila? Šlepujemo sve vrste
            putničkih i lakih teretnih vozila po najboljim cenama — bilo gde u Srbiji.
          </p>

          <ul className="mt-8 space-y-3.5">
            {FEATURES.map((f) => (
              <li key={f} className="flex items-center gap-3 text-zinc-300 text-sm">
                <CheckCircle2 size={18} className="text-red-500 shrink-0" />
                {f}
              </li>
            ))}
          </ul>

          <div className="mt-10 flex flex-wrap items-center gap-5">
            <a data-testid="towing-call-now-button" href="tel:0677775613"
              className="animate-pulse-ring flex items-center gap-3 bg-red-600 hover:bg-red-500 text-white font-bold px-8 py-4 rounded-full transition-all hover:shadow-[0_0_36px_rgba(227,22,30,0.6)] active:scale-[0.97]">
              <Phone size={19} />
              Pozovi Šlep Službu
            </a>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Dostupni 00-24h</div>
              <div className="font-display text-2xl text-white tracking-wider">067 777 5613</div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="relative"
        >
          <div className="relative rounded-3xl overflow-hidden border border-red-600/25 shadow-[0_20px_80px_rgba(227,22,30,0.18)] aspect-[4/3]">
            <motion.img style={{ y: imgY }} src={TOW_IMG} alt="AutoBay šlep služba"
              className="w-full h-[116%] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[rgba(9,9,11,0.75)] to-transparent" />
            <div className="absolute bottom-5 left-5 flex items-center gap-3 bg-[rgba(9,9,11,0.8)] backdrop-blur-md border border-white/10 rounded-full px-5 py-2.5">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-sm font-semibold text-white">Ekipa spremna za slanje</span>
            </div>
          </div>
          <div className="absolute -top-4 -right-4 bg-red-600 text-white font-display text-lg tracking-wider px-5 py-2.5 rounded-xl rotate-3 shadow-lg">
            24/7/365
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        data-testid="towing-calculator"
        className="relative max-w-7xl mx-auto mt-16 bg-[#121216] border border-red-600/25 rounded-3xl p-7 sm:p-10 overflow-hidden"
      >
        <div className="absolute -top-20 -left-20 w-64 h-64 rounded-full bg-red-600/10 blur-3xl pointer-events-none" />
        <div className="relative flex flex-col lg:flex-row lg:items-center gap-8">
          <div className="lg:w-72 shrink-0">
            <span className="flex items-center gap-2 text-red-400 text-xs font-bold tracking-[0.25em] uppercase">
              <Calculator size={14} /> Kalkulator šlepanja
            </span>
            <h3 className="font-display text-3xl text-white mt-3 leading-none">Proceni Cenu Prevoza</h3>
            <p className="text-xs text-zinc-500 mt-3 leading-relaxed">
              Izaberite tip vozila i relaciju. Cene su usklađene sa cenovnicima šlep službi u Srbiji — putarine i izvlačenje van kolovoza se doplaćuju, noćna tarifa se uključuje prekidačem.
            </p>
          </div>
          <div className="flex-1">
            <div data-testid="towing-vehicle-types" className="flex flex-wrap gap-2 mb-4">
              {VEHICLE_TYPES.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  data-testid={`towing-vehicle-${v.id}`}
                  onClick={() => { setVehicleType(v.id); setEstimate(null); }}
                  className={`flex items-center gap-2 text-xs font-semibold px-4 py-2.5 rounded-full border transition-all active:scale-[0.96] ${
                    vehicleType === v.id
                      ? "bg-red-600 border-red-500 text-white shadow-[0_0_18px_rgba(227,22,30,0.4)]"
                      : "bg-[#0c0c0f] border-white/10 text-zinc-400 hover:border-red-600/40 hover:text-white"
                  }`}
                >
                  <v.icon size={14} />
                  {v.label}
                </button>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
            <div className="relative">              <MapPin size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-red-500" />
              <input
                data-testid="towing-pickup-input"
                type="text"
                value={pickup}
                onChange={(e) => setPickup(e.target.value)}
                placeholder="Mesto preuzimanja (npr. Beograd)"
                className="w-full bg-[#0c0c0f] border border-white/10 focus:border-red-500/70 rounded-xl pl-11 pr-4 py-3.5 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors"
              />
            </div>
            <div className="relative">
              <Navigation size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-red-500" />
              <input
                data-testid="towing-destination-input"
                type="text"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                placeholder="Destinacija (npr. Novi Sad)"
                className="w-full bg-[#0c0c0f] border border-white/10 focus:border-red-500/70 rounded-xl pl-11 pr-4 py-3.5 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors"
              />
            </div>
          </div>
          </div>
          <div className="shrink-0 flex flex-col items-stretch gap-2.5">
            <button
              data-testid="towing-calculate-button"
              onClick={calculate}
              disabled={loading}
              className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white font-bold px-8 py-3.5 rounded-full transition-all hover:shadow-[0_0_28px_rgba(227,22,30,0.5)] active:scale-[0.97]"
            >
              {loading ? <Loader2 size={17} className="animate-spin" /> : <Calculator size={17} />}
              {loading ? "Računam..." : "Izračunaj"}
            </button>
            <button
              type="button"
              data-testid="towing-night-toggle"
              onClick={() => { setNight(!night); setEstimate(null); }}
              className={`flex items-center justify-center gap-2 text-xs font-semibold px-4 py-2.5 rounded-full border transition-all active:scale-[0.96] ${
                night
                  ? "bg-indigo-500/15 border-indigo-400/50 text-indigo-300"
                  : "bg-[#0c0c0f] border-white/10 text-zinc-500 hover:text-white"
              }`}
            >
              <Moon size={13} />
              Noćna tarifa 22-06h (+10%)
              <span className={`w-2 h-2 rounded-full ${night ? "bg-indigo-400" : "bg-zinc-700"}`} />
            </button>
          </div>
        </div>

        {estimate && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            data-testid="towing-estimate-result"
            className="relative mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-5 bg-[#0c0c0f] border border-white/10 rounded-2xl p-6"
          >
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Relacija · {estimate.vehicle_label}</div>
              <div className="text-sm text-zinc-300 mt-1">{estimate.pickup_label.split(",").slice(0, 2).join(",")} → {estimate.destination_label.split(",").slice(0, 2).join(",")}</div>
              <div className="text-[11px] text-zinc-600 mt-1.5">Start {estimate.base_rsd.toLocaleString("sr-RS")} RSD + {estimate.per_km_rsd} RSD/km</div>
              {estimate.night && (
                <div data-testid="towing-night-badge" className="flex items-center gap-1.5 text-[11px] font-semibold text-indigo-300 mt-1.5">
                  <Moon size={11} /> Noćna tarifa +10% uračunata
                </div>
              )}
            </div>
            <div className="sm:text-center">
              <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Udaljenost</div>
              <div data-testid="towing-estimate-distance" className="font-display text-3xl text-white mt-1">~{estimate.distance_km} km</div>
            </div>
            <div className="sm:text-right">
              <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Procena cene</div>
              <div data-testid="towing-estimate-price" className="font-display text-4xl text-red-500 mt-1">{estimate.price_rsd.toLocaleString("sr-RS")} RSD</div>
            </div>
            <a href="tel:0677775613"
              className="sm:ml-2 flex items-center justify-center gap-2 border border-red-600/50 hover:bg-red-600 text-white text-sm font-semibold px-6 py-3 rounded-full transition-all active:scale-[0.97]">
              <Phone size={15} /> Potvrdi Pozivom
            </a>
          </motion.div>
        )}
      </motion.div>
    </section>
  );
}
